import React, { useState, useEffect, useContext } from 'react';
import { User, Star, Package, TrendingUp, DollarSign, Calendar, Clock, CheckCircle2, XCircle, AlertCircle, Eye, Award, ShoppingBag, ChevronRight, Filter, Download, RefreshCw, Info, CreditCard, AlertTriangle, Tag, Palette, Shirt, Users, MapPin, CheckCircle, Play, CircleDot } from 'lucide-react';
import Base from './Base';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';

const DesignerProfile = () => {
  const { authToken } = useContext(AuthContext);
  const [profileData, setProfileData] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [payoutFilter, setPayoutFilter] = useState('all');
  const [payoutSearch, setPayoutSearch] = useState('');

  useEffect(() => {
    fetchDesignerData();
  }, [authToken]);

  const fetchDesignerData = async () => {
    try {
      setLoading(true);

      // Fetch designer profile
      const profileResponse = await axios.get('https://vasstrinfo.com/api/designer/profile/', {
        headers: {
          Authorization: `Bearer ${authToken?.access}`,
          'Content-Type': 'application/json',
        },
      });

      setProfileData(profileResponse.data.data);

      // Fetch payout history
      try {
        const payoutResponse = await axios.get('https://vasstrinfo.com/api/designer/payouts/', {
          headers: {
            Authorization: `Bearer ${authToken?.access}`,
            'Content-Type': 'application/json',
          },
        });
        setPayoutHistory(payoutResponse.data.data?.payouts || []);
      } catch (payoutError) {
        console.log('No payout history found');
        setPayoutHistory([]);
      }

    } catch (error) {
      console.error('Error fetching designer data:', error);
      if (error.response?.status === 404) {
        Swal.fire({
          title: 'No Designer Profile',
          text: 'Please complete your KYC first to view your designer profile.',
          icon: 'info',
          confirmButtonColor: '#ea580c'
        });
      } else {
        Swal.fire('Error', 'Failed to fetch designer profile data', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'under_review':
        return <Clock className="h-5 w-5 text-orange-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getPayoutStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'processing':
        return <Clock className="h-4 w-4 text-orange-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getApprovalStatusBadge = (approvalStatus) => {
    if (!approvalStatus) return null;

    const statusConfig = {
      'pending_review': { bg: 'bg-orange-100', text: 'text-orange-800', label: '⏳ Pending Review' },
      'approved': { bg: 'bg-green-100', text: 'text-green-800', label: '✓ Approved' },
      'rejected': { bg: 'bg-red-100', text: 'text-red-800', label: '✗ Rejected' }
    };

    const config = statusConfig[approvalStatus] || statusConfig['pending_review'];

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredPayouts = () => {
    let filtered = payoutHistory;

    // Filter by status
    if (payoutFilter !== 'all') {
      filtered = filtered.filter(payout => payout.payout_status === payoutFilter);
    }

    // Filter by search term
    if (payoutSearch) {
      filtered = filtered.filter(payout =>
        payout.order_id?.toString().toLowerCase().includes(payoutSearch.toLowerCase()) ||
        payout.razorpay_payout_id?.toLowerCase().includes(payoutSearch.toLowerCase())
      );
    }

    return filtered;
  };

  const getStatusCount = (status) => {
    return payoutHistory.filter(payout => payout.payout_status === status).length;
  };

  const calculateTotalEarnings = () => {
    return payoutHistory
      .filter(payout => payout.payout_status === 'success')
      .reduce((total, payout) => total + parseFloat(payout.net_amount), 0);
  };

  const getPendingAmount = () => {
    return payoutHistory
      .filter(payout => payout.payout_status === 'pending' || payout.payout_status === 'processing')
      .reduce((total, payout) => total + parseFloat(payout.net_amount), 0);
  };

  const getTotalCommission = () => {
    return payoutHistory
      .reduce((total, payout) => total + parseFloat(payout.commission_amount || 0), 0);
  };

  const getTotalGrossAmount = () => {
    return payoutHistory
      .reduce((total, payout) => total + parseFloat(payout.amount || payout.order_price || 0), 0);
  };

  const getLifetimeStats = () => {
    const totalOrders = payoutHistory.length;
    const successfulPayouts = payoutHistory.filter(p => p.payout_status === 'success').length;
    const successRate = totalOrders > 0 ? ((successfulPayouts / totalOrders) * 100).toFixed(1) : 0;

    return {
      totalOrders,
      successRate,
      totalEarnings: calculateTotalEarnings(),
      totalCommission: getTotalCommission(),
      grossRevenue: getTotalGrossAmount()
    };
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-600">Loading designer profile...</p>
          </div>
        </div>
      } />
    );
  }

  if (!profileData) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md">
            <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Designer Profile</h2>
            <p className="text-gray-600 mb-6">Please complete your KYC first to view your designer profile.</p>
            <button
              onClick={() => window.location.href = '/designer-onboarding'}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Complete KYC
            </button>
          </div>
        </div>
      } />
    );
  }

  return (
    <Base Content={
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="bg-white rounded-full p-3">
                    <User className="h-8 w-8 text-orange-500" />
                  </div>
                  <div className="text-white">
                    <h1 className="text-3xl font-bold">{profileData.name}</h1>
                    <p className="text-orange-100">{profileData.email}</p>
                    <p className="text-orange-100">{profileData.phone}</p>
                  </div>
                </div>
                <div className="text-right text-white">
                  <div className="flex items-center space-x-2 mb-2">
                    {getStatusIcon(profileData.onboarding_status)}
                    <span className="font-semibold capitalize">{profileData.onboarding_status}</span>
                  </div>
                  <p className="text-sm text-orange-100">Member since {formatDate(profileData.created_at)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Stats Dashboard */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <TrendingUp className="h-6 w-6 mr-2 text-orange-500" />
              Payment Performance Dashboard
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Earnings */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="h-8 w-8 text-green-600" />
                  <span className="text-xs text-green-600 font-medium">NET EARNINGS</span>
                </div>
                <p className="text-2xl font-bold text-green-700">₹{calculateTotalEarnings().toLocaleString()}</p>
                <p className="text-sm text-green-600 mt-1">After platform commission</p>
              </div>

              {/* Gross Revenue */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <Package className="h-8 w-8 text-blue-600" />
                  <span className="text-xs text-blue-600 font-medium">GROSS REVENUE</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">₹{getTotalGrossAmount().toLocaleString()}</p>
                <p className="text-sm text-blue-600 mt-1">Total order value</p>
              </div>

              {/* Platform Commission */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <CreditCard className="h-8 w-8 text-gray-600" />
                  <span className="text-xs text-gray-600 font-medium">COMMISSION PAID</span>
                </div>
                <p className="text-2xl font-bold text-gray-700">₹{getTotalCommission().toLocaleString()}</p>
                <p className="text-sm text-gray-600 mt-1">Platform fees</p>
              </div>

              {/* Success Rate */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <CheckCircle2 className="h-8 w-8 text-purple-600" />
                  <span className="text-xs text-purple-600 font-medium">SUCCESS RATE</span>
                </div>
                <p className="text-2xl font-bold text-purple-700">{getLifetimeStats().successRate}%</p>
                <p className="text-sm text-purple-600 mt-1">{getLifetimeStats().totalOrders} total orders</p>
              </div>
            </div>

            {/* Quick Stats Bar */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-600">Pending Amount</p>
                  <p className="text-lg font-bold text-orange-600">₹{getPendingAmount().toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Successful Payouts</p>
                  <p className="text-lg font-bold text-green-600">{getStatusCount('success')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Failed Payouts</p>
                  <p className="text-lg font-bold text-red-600">{getStatusCount('failed')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Processing</p>
                  <p className="text-lg font-bold text-orange-600">{getStatusCount('processing')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'overview'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Eye className="h-4 w-4 inline mr-2" />
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('payouts')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'payouts'
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <DollarSign className="h-4 w-4 inline mr-2" />
                  Payout History
                </button>
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* KYC Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">KYC Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Personal Details</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Name:</span>
                            <span className="font-medium">{profileData.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Email:</span>
                            <span className="font-medium">{profileData.email}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Phone:</span>
                            <span className="font-medium">{profileData.phone}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Business Type:</span>
                            <span className="font-medium capitalize">{profileData.business_type}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Tax & Banking</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">PAN:</span>
                            <span className="font-medium font-mono">{profileData.pan || 'Not provided'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">GSTIN:</span>
                            <span className="font-medium font-mono">{profileData.gstin || 'Not provided'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Bank Account:</span>
                            <span className="font-medium font-mono">
                              {profileData.bank_account_number ? `****${profileData.bank_account_number.slice(-4)}` : 'Not provided'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">IFSC:</span>
                            <span className="font-medium font-mono">{profileData.ifsc_code || 'Not provided'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Address Information */}
                    <div className="bg-gray-50 rounded-lg p-4 mt-6">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                        <MapPin className="h-4 w-4 mr-2 text-orange-500" />
                        Business Address
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-600 block">Street Address Line 1:</span>
                          <span className="font-medium">{profileData.street_address_1 || 'Not provided'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600 block">Street Address Line 2:</span>
                          <span className="font-medium">{profileData.street_address_2 || 'Not provided'}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-gray-600 block">City:</span>
                            <span className="font-medium">{profileData.city || 'Not provided'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600 block">State:</span>
                            <span className="font-medium">{profileData.state || 'Not provided'}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-gray-600 block">Postal Code:</span>
                            <span className="font-medium">{profileData.postal_code || 'Not provided'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600 block">Country:</span>
                            <span className="font-medium">{profileData.country || 'IN'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Account Status */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Status</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(profileData.onboarding_status)}
                          <div>
                            <p className="font-medium capitalize">{profileData.onboarding_status}</p>
                            <p className="text-sm text-gray-600">
                              {profileData.onboarding_status === 'approved' && 'Your account is fully verified and you can receive payouts.'}
                              {profileData.onboarding_status === 'under_review' && 'Your KYC documents are being reviewed.'}
                              {profileData.onboarding_status === 'rejected' && 'Your KYC was rejected. Please resubmit with correct information.'}
                              {profileData.onboarding_status === 'pending' && 'Please complete your KYC verification.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Razorpay Integration Status */}
                  {(profileData.razorpay_account_id || profileData.razorpay_stakeholder_id || profileData.razorpay_bank_account_id || profileData.razorpay_product_id) && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <CreditCard className="h-5 w-5 mr-2 text-orange-500" />
                        Razorpay Payment Integration
                      </h3>
                      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-lg p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          {profileData.razorpay_account_id && (
                            <div className="bg-white rounded-lg p-3 border border-orange-200">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Linked Account ID</label>
                              <p className="font-mono text-sm text-gray-900 break-all">{profileData.razorpay_account_id}</p>
                            </div>
                          )}
                          {profileData.razorpay_stakeholder_id && (
                            <div className="bg-white rounded-lg p-3 border border-orange-200">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Stakeholder ID</label>
                              <p className="font-mono text-sm text-gray-900 break-all">{profileData.razorpay_stakeholder_id}</p>
                            </div>
                          )}
                          {profileData.razorpay_bank_account_id && (
                            <div className="bg-white rounded-lg p-3 border border-orange-200">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Bank Account ID</label>
                              <p className="font-mono text-sm text-gray-900 break-all">{profileData.razorpay_bank_account_id}</p>
                            </div>
                          )}
                          {profileData.razorpay_product_id && (
                            <div className="bg-white rounded-lg p-3 border border-orange-200">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Product Configuration ID</label>
                              <p className="font-mono text-sm text-gray-900 break-all">{profileData.razorpay_product_id}</p>
                            </div>
                          )}
                        </div>

                        {profileData.razorpay_activation_status && (
                          <div className="bg-white rounded-lg p-3 border border-orange-200 mb-4">
                            <label className="block text-xs font-medium text-gray-600 mb-2">Razorpay Activation Status</label>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                              profileData.razorpay_activation_status === 'activated'
                                ? 'bg-green-100 text-green-800'
                                : profileData.razorpay_activation_status === 'needs_clarification'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {profileData.razorpay_activation_status === 'activated' && '✓ Activated - Ready to Receive Payments'}
                              {profileData.razorpay_activation_status === 'created' && '⏳ Under Review by Razorpay'}
                              {profileData.razorpay_activation_status === 'needs_clarification' && '⚠️ Needs Clarification'}
                            </span>
                          </div>
                        )}

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-start space-x-2">
                            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-blue-800">
                              <p className="font-semibold mb-1">How Payouts Work:</p>
                              <ul className="space-y-1 list-disc list-inside">
                                <li>When a customer completes an order, admin creates a payout request</li>
                                <li>Platform commission (3%) is deducted from the order amount</li>
                                <li>After admin approval, the remaining amount (97%) is transferred to your linked bank account via Razorpay Route</li>
                                <li>You receive an email notification when the payout is processed</li>
                                <li>Track all your payouts in the "Payout History" tab above</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'payouts' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Orders & Payout History</h2>
                  <p className="text-gray-600 mb-6">Complete details of all your orders with payout status</p>

                  {/* Payout Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-600">Successful Payouts</p>
                          <p className="text-2xl font-bold text-green-700">{getStatusCount('success')}</p>
                        </div>
                        <CheckCircle2 className="h-8 w-8 text-green-500" />
                      </div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-orange-600">Processing</p>
                          <p className="text-2xl font-bold text-orange-700">{getStatusCount('processing')}</p>
                        </div>
                        <Clock className="h-8 w-8 text-orange-500" />
                      </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Pending</p>
                          <p className="text-2xl font-bold text-gray-700">{getStatusCount('pending')}</p>
                        </div>
                        <AlertCircle className="h-8 w-8 text-gray-500" />
                      </div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-red-600">Failed</p>
                          <p className="text-2xl font-bold text-red-700">{getStatusCount('failed')}</p>
                        </div>
                        <XCircle className="h-8 w-8 text-red-500" />
                      </div>
                    </div>
                  </div>

                  {/* Filters and Search */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Filter className="h-4 w-4 text-gray-500" />
                        <select
                          value={payoutFilter}
                          onChange={(e) => setPayoutFilter(e.target.value)}
                          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        >
                          <option value="all">All Payouts ({payoutHistory.length})</option>
                          <option value="success">Successful ({getStatusCount('success')})</option>
                          <option value="processing">Processing ({getStatusCount('processing')})</option>
                          <option value="pending">Pending ({getStatusCount('pending')})</option>
                          <option value="failed">Failed ({getStatusCount('failed')})</option>
                        </select>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search by Order ID or Razorpay ID..."
                          value={payoutSearch}
                          onChange={(e) => setPayoutSearch(e.target.value)}
                          className="border border-gray-300 rounded-lg px-3 py-2 pl-8 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 w-64"
                        />
                        <Package className="h-4 w-4 text-gray-500 absolute left-2 top-3" />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={fetchDesignerData}
                        className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                      >
                        <RefreshCw className="h-4 w-4" />
                        <span>Refresh</span>
                      </button>
                      <button className="flex items-center space-x-2 px-3 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600">
                        <Download className="h-4 w-4" />
                        <span>Export</span>
                      </button>
                    </div>
                  </div>

                  {getFilteredPayouts().length === 0 ? (
                    <div className="text-center py-12">
                      <ShoppingBag className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {payoutHistory.length === 0 ? 'No Payouts Yet' : 'No Matching Payouts'}
                      </h3>
                      <p className="text-gray-600">
                        {payoutHistory.length === 0
                          ? 'Your payout history will appear here once you receive orders.'
                          : 'Try adjusting your filters to see more results.'}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Orders & Payouts Grid */}
                      <div className="space-y-6 mb-6">
                        {getFilteredPayouts().map((payout, index) => (
                          <div key={index} className="bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:shadow-lg transition-all overflow-hidden">

                            {/* Main Content Grid - Order Details on Left, Payout Status on Right */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">

                              {/* LEFT SECTION: ORDER DETAILS (2/3 width) */}
                              <div className="lg:col-span-2 p-6 border-r border-gray-200">
                                {/* Order Header */}
                                <div className="flex items-start justify-between mb-4">
                                  <div>
                                    <h3 className="text-xl font-bold text-gray-900 flex items-center">
                                      <Package className="h-5 w-5 mr-2 text-orange-500" />
                                      Order #{payout.order_id}
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1">Ordered on {formatDate(payout.order_created_at)}</p>
                                  </div>
                                </div>

                                {/* Product Information */}
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                                  <h4 className="font-semibold text-orange-900 mb-3 flex items-center">
                                    <Shirt className="h-4 w-4 mr-2" />
                                    {payout.product_name}
                                  </h4>
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                    <div>
                                      <span className="text-gray-600 block">Type:</span>
                                      <span className="font-medium text-gray-900">{payout.product_type || 'N/A'}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600 block">Quantity:</span>
                                      <span className="font-medium text-gray-900">{payout.quantity}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600 block">Size:</span>
                                      <span className="font-medium text-gray-900">{payout.size || 'N/A'}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600 block">Gender:</span>
                                      <span className="font-medium text-gray-900">{payout.gender || 'N/A'}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600 block">Color:</span>
                                      <span className="font-medium text-gray-900">{payout.color || 'N/A'}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600 block">Fabric:</span>
                                      <span className="font-medium text-gray-900">{payout.fabric || 'N/A'}</span>
                                    </div>
                                  </div>
                                  {payout.description && (
                                    <div className="mt-3 pt-3 border-t border-orange-200">
                                      <span className="text-gray-600 text-sm block mb-1">Description:</span>
                                      <p className="text-sm text-gray-700">{payout.description}</p>
                                    </div>
                                  )}
                                </div>

                                {/* Customer Information */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                                    <Users className="h-4 w-4 mr-2" />
                                    Customer Details
                                  </h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                    <div>
                                      <span className="text-gray-600 block">Name:</span>
                                      <span className="font-medium text-gray-900">{payout.customer_name}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600 block">Phone:</span>
                                      <span className="font-medium text-gray-900">{payout.customer_phone}</span>
                                    </div>
                                    <div className="md:col-span-2">
                                      <span className="text-gray-600 block">Email:</span>
                                      <span className="font-medium text-gray-900 text-xs">{payout.customer_email}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Shipping Address */}
                                {payout.shipping_address && (
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                                      <MapPin className="h-4 w-4 mr-2" />
                                      Shipping Address
                                    </h4>
                                    <p className="text-sm text-gray-700">
                                      {payout.shipping_address.street}, {payout.shipping_address.city}<br />
                                      {payout.shipping_address.state} - {payout.shipping_address.pincode}<br />
                                      {payout.shipping_address.country}
                                    </p>
                                  </div>
                                )}

                                {/* Order Status Timeline */}
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">Order Progress:</h4>
                                  <div className="grid grid-cols-4 gap-2">
                                    <div className={`text-center p-2 rounded ${payout.talked_to_customer ? 'bg-green-100' : 'bg-gray-100'}`}>
                                      <CheckCircle className={`h-4 w-4 mx-auto mb-1 ${payout.talked_to_customer ? 'text-green-600' : 'text-gray-400'}`} />
                                      <span className={`text-xs ${payout.talked_to_customer ? 'text-green-700 font-medium' : 'text-gray-500'}`}>Contacted</span>
                                    </div>
                                    <div className={`text-center p-2 rounded ${payout.started_order ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                      <Play className={`h-4 w-4 mx-auto mb-1 ${payout.started_order ? 'text-blue-600' : 'text-gray-400'}`} />
                                      <span className={`text-xs ${payout.started_order ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>Started</span>
                                    </div>
                                    <div className={`text-center p-2 rounded ${payout.finished_order ? 'bg-purple-100' : 'bg-gray-100'}`}>
                                      <CircleDot className={`h-4 w-4 mx-auto mb-1 ${payout.finished_order ? 'text-purple-600' : 'text-gray-400'}`} />
                                      <span className={`text-xs ${payout.finished_order ? 'text-purple-700 font-medium' : 'text-gray-500'}`}>Finished</span>
                                    </div>
                                    <div className={`text-center p-2 rounded ${payout.delivered_order ? 'bg-green-100' : 'bg-gray-100'}`}>
                                      <Package className={`h-4 w-4 mx-auto mb-1 ${payout.delivered_order ? 'text-green-600' : 'text-gray-400'}`} />
                                      <span className={`text-xs ${payout.delivered_order ? 'text-green-700 font-medium' : 'text-gray-500'}`}>Delivered</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* RIGHT SECTION: PAYOUT STATUS (1/3 width) */}
                              <div className="lg:col-span-1 bg-gradient-to-br from-green-50 to-blue-50 p-6">
                                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                                  <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                                  Payout Status
                                </h4>

                                {/* Status Badges */}
                                <div className="space-y-2 mb-6">
                                  {payout.approval_status && (
                                    <div>
                                      <span className="text-xs text-gray-600 block mb-1">Approval:</span>
                                      {getApprovalStatusBadge(payout.approval_status)}
                                    </div>
                                  )}
                                  <div>
                                    <span className="text-xs text-gray-600 block mb-1">Payment:</span>
                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                      payout.payout_status === 'success' ? 'bg-green-100 text-green-800' :
                                      payout.payout_status === 'processing' ? 'bg-orange-100 text-orange-800' :
                                      payout.payout_status === 'pending' ? 'bg-gray-100 text-gray-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {payout.payout_status.charAt(0).toUpperCase() + payout.payout_status.slice(1)}
                                    </span>
                                  </div>
                                </div>

                                {/* Payment Breakdown */}
                                <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                                  <h5 className="font-semibold text-gray-900 mb-3 text-sm">Payment Breakdown</h5>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Order Amount:</span>
                                      <span className="font-medium">₹{parseFloat(payout.order_amount).toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-red-600">
                                      <span>Commission ({payout.commission_percentage}%):</span>
                                      <span className="font-medium">-₹{parseFloat(payout.commission_amount).toLocaleString()}</span>
                                    </div>
                                    <div className="pt-2 border-t border-gray-200">
                                      <div className="flex justify-between items-center">
                                        <span className="font-semibold text-gray-900">You Receive:</span>
                                        <span className="font-bold text-green-600 text-lg">₹{parseFloat(payout.net_amount).toLocaleString()}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Transaction Details */}
                                {payout.razorpay_payout_id && (
                                  <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                                    <h5 className="font-semibold text-gray-900 mb-2 text-sm">Transaction ID</h5>
                                    <p className="font-mono text-xs text-gray-700 break-all">{payout.razorpay_payout_id}</p>
                                  </div>
                                )}

                                {/* Dates */}
                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                  <div className="space-y-2 text-xs">
                                    <div>
                                      <span className="text-gray-600 block">Payout Created:</span>
                                      <span className="font-medium text-gray-900">{formatDate(payout.payout_created_at)}</span>
                                    </div>
                                    {payout.processed_at && (
                                      <div>
                                        <span className="text-gray-600 block">Processed:</span>
                                        <span className="font-medium text-gray-900">{formatDate(payout.processed_at)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Approval/Rejection Message */}
                                {payout.approval_status === 'pending_review' && (
                                  <div className="mt-4 bg-orange-100 border border-orange-300 rounded-lg p-3">
                                    <p className="text-xs text-orange-800">
                                      <AlertCircle className="h-4 w-4 inline mr-1" />
                                      Awaiting admin approval
                                    </p>
                                  </div>
                                )}

                                {payout.approval_status === 'approved' && payout.approved_at && (
                                  <div className="mt-4 bg-green-100 border border-green-300 rounded-lg p-3">
                                    <p className="text-xs text-green-800">
                                      <CheckCircle2 className="h-4 w-4 inline mr-1" />
                                      Approved {payout.approved_by_username && `by ${payout.approved_by_username}`}
                                    </p>
                                  </div>
                                )}

                                {payout.rejection_reason && (
                                  <div className="mt-4 bg-red-100 border border-red-300 rounded-lg p-3">
                                    <p className="text-xs text-red-800 font-semibold mb-1">
                                      <XCircle className="h-4 w-4 inline mr-1" />
                                      Rejected
                                    </p>
                                    <p className="text-xs text-red-700">{payout.rejection_reason}</p>
                                  </div>
                                )}

                                {payout.failure_reason && !payout.rejection_reason && (
                                  <div className="mt-4 bg-red-100 border border-red-300 rounded-lg p-3">
                                    <p className="text-xs text-red-800 font-semibold mb-1">
                                      <AlertTriangle className="h-4 w-4 inline mr-1" />
                                      Transfer Failed
                                    </p>
                                    <p className="text-xs text-red-700">{payout.failure_reason}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    } />
  );
};

export default DesignerProfile;
